import { useSessionTokenGetter } from '@develio/auth/client';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

type ProjectType = 'new_startup' | 'expansion' | 'retention';

type CategoryBreakdown = {
  readonly score: number;
  readonly weight: number;
  readonly contribution: number;
  readonly notes: string[];
  readonly notApplicable?: boolean;
};

type ScoreData = {
  readonly grade: string;
  readonly score: number;
  readonly gradeColor: string;
  readonly paybackFlag: boolean;
  readonly projectType?: ProjectType;
  readonly advisory?: boolean;
  readonly advisoryReason?: string | null;
  readonly breakdown: Record<string, CategoryBreakdown>;
};

type Analysis = {
  readonly id: string;
  readonly projectName: string;
  readonly createdAt: string;
  readonly scoreData: ScoreData | null;
};

const PROJECT_TYPE_LABELS: Record<ProjectType, string> = {
  new_startup: 'New project',
  expansion: 'Expansion',
  retention: 'Retention',
};

/**
 * Per-category descriptive copy. Sits next to the rationale notes so the
 * analyst sees both *what* the engine concluded and *why this category
 * matters* in plain English. Stays in this file (not the scoring engine)
 * because it's audience-facing prose, not methodology.
 */
const CATEGORY_META: Record<string, { label: string; explainer: string }> = {
  fiscalReturn: {
    label: 'Fiscal Return',
    explainer:
      'Speed and magnitude of the return on incentive dollars — payback period, ROI, and benefit-cost ratio.',
  },
  jobCreation: {
    label: 'Job Creation',
    explainer:
      'Cost-efficiency of jobs supported per incentive dollar. For retention projects, this measures employment preserved.',
  },
  wageQuality: {
    label: 'Wage Quality',
    explainer:
      'Average wage relative to the local benchmark — higher means stronger household impact.',
  },
  capitalInvestment: {
    label: 'Capital Investment',
    explainer:
      'Capital invested per direct job — proxy for productive capacity and tax-base contribution.',
  },
  netFiscalImpact: {
    label: 'Net Fiscal Impact',
    explainer:
      '10-year NPV per total job created — the long-run dollars-on-the-table view, net of incentives.',
  },
};

function labelFor(key: string, projectType: ProjectType | undefined): string {
  if (key === 'jobCreation' && projectType === 'retention') return 'Employment Impact';
  return CATEGORY_META[key]?.label ?? key;
}

export function AnalysisScoreRoute() {
  const { id } = useParams();
  const getToken = useSessionTokenGetter();
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/analyses/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`${res.status}`);
        const data = (await res.json()) as { analysis: Analysis };
        if (!cancelled) setAnalysis(data.analysis);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, getToken]);

  if (error) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Could not load score: {error}
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="mx-auto max-w-3xl p-6 text-sm text-slate-500">Loading score breakdown…</div>
    );
  }

  if (!analysis.scoreData) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <Link to={`/analyses/${analysis.id}`} className="text-xs text-slate-400 hover:underline">
          ← {analysis.projectName}
        </Link>
        <div className="mt-4 rounded-md border border-slate-200 bg-white p-4 text-sm text-slate-600">
          No score data available for this analysis. Re-run with scoring enabled to generate a
          grade.
        </div>
      </div>
    );
  }

  const score = analysis.scoreData;
  const entries = Object.entries(score.breakdown);

  return (
    <div className="mx-auto max-w-4xl p-6">
      <Link to={`/analyses/${analysis.id}`} className="text-xs text-slate-400 hover:underline">
        ← {analysis.projectName}
      </Link>

      {/* Hero */}
      <div className="mt-3 flex flex-wrap items-start gap-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div
          className="flex h-28 w-28 shrink-0 flex-col items-center justify-center rounded-2xl text-white"
          style={{ backgroundColor: score.gradeColor }}
        >
          <div className="font-display text-5xl font-extrabold leading-none">{score.grade}</div>
          <div className="mt-1 text-xs font-semibold uppercase tracking-wider opacity-90">
            {score.score.toFixed(1)} / 100
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-2xl font-semibold tracking-tight text-slate-900">
            Project Score
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Composite of {entries.filter(([, c]) => !c.notApplicable).length} weighted categories,
            scored against your organization's rubric for{' '}
            <strong className="text-slate-800">
              {score.projectType ? PROJECT_TYPE_LABELS[score.projectType] : 'standard'}
            </strong>{' '}
            projects.
          </p>
          {score.advisory && score.advisoryReason && (
            <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <strong className="uppercase tracking-wider">Advisory:</strong> {score.advisoryReason}
            </div>
          )}
          {score.paybackFlag && (
            <div className="mt-2 text-xs text-amber-800">
              * Payback period not applicable — excluded from fiscal-return scoring.
            </div>
          )}
        </div>
      </div>

      {/* Category cards */}
      <div className="mt-6 space-y-3">
        {entries.map(([key, cat]) => {
          const na = cat.notApplicable === true;
          const label = labelFor(key, score.projectType);
          const explainer = CATEGORY_META[key]?.explainer ?? '';
          return (
            <div
              key={key}
              className={`rounded-xl border p-5 transition ${
                na
                  ? 'border-slate-200 bg-slate-50 opacity-70'
                  : 'border-slate-200 bg-white shadow-sm'
              }`}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div className="flex items-baseline gap-2">
                  <h3 className="font-display text-lg font-semibold text-slate-900">{label}</h3>
                  {na && (
                    <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-600">
                      N/A
                    </span>
                  )}
                </div>
                {!na && (
                  <div className="flex items-baseline gap-4 text-sm tabular-nums">
                    <span className="text-slate-500">
                      Weight <strong className="text-slate-800">{cat.weight.toFixed(0)}%</strong>
                    </span>
                    <span className="text-slate-500">
                      Score <strong className="text-slate-800">{cat.score.toFixed(1)}</strong>
                    </span>
                    <span className="text-slate-500">
                      Points{' '}
                      <strong className="text-slate-800">{cat.contribution.toFixed(1)}</strong>
                    </span>
                  </div>
                )}
              </div>

              {explainer && <p className="mt-1.5 text-xs text-slate-500">{explainer}</p>}

              {!na && (
                <div className="mt-3">
                  <ScoreBar score={cat.score} />
                </div>
              )}

              {!na && cat.notes.length > 0 && (
                <ul className="mt-3 space-y-1.5 border-t border-slate-100 pt-3 text-sm text-slate-700">
                  {cat.notes.map((note, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: notes are plain strings with no stable id
                    <li key={i} className="flex gap-2">
                      <span className="mt-0.5 text-slate-400">•</span>
                      <span>{note}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-8 text-center text-xs text-slate-400">
        Scoring methodology configured in{' '}
        <Link to="/settings/scoring" className="text-indigo-600 hover:underline">
          Settings → Scoring
        </Link>
        . Scores are locked at the time the analysis is run.
      </div>
    </div>
  );
}

/**
 * Horizontal score bar (0–100). Color tracks the typical EDC traffic-light
 * pattern: red < 60, amber 60–80, green ≥ 80.
 */
function ScoreBar({ score }: { score: number }) {
  const clamped = Math.max(0, Math.min(100, score));
  const color = clamped >= 80 ? '#15803D' : clamped >= 60 ? '#B45309' : '#B91C1C';
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
      <div
        className="h-full rounded-full transition-all"
        style={{ width: `${clamped}%`, backgroundColor: color }}
      />
    </div>
  );
}
